/**
 * §P.9 fase 2 · Ocupación por franja.
 *
 * ## Es para responder «dónde cabe un grupo nuevo»
 *
 * Eso es lo que dice el contrato que hace, y por eso la pantalla destaca **las
 * franjas con plazas de sobra** en lugar de dar una tabla ordenada por día. Un club
 * que quiere abrir un grupo no necesita saber la ocupación media: necesita saber qué
 * martes a las seis tiene sitio.
 *
 * ## Sin aforo puesto no hay porcentaje
 *
 * «Devolver 0 % ahí sería inventarse un aforo infinito.» Así que esas franjas dicen
 * «sin aforo puesto» y **no cuentan** como sitio libre: no se sabe si sobran plazas o
 * si el club nunca las contó, y mandar a alguien a una sala llena por eso es peor que
 * no decir nada.
 */
import { api } from '@/shared/api/client';
import { ErrorDeApi, aProblema } from '@/shared/api/errores';
import { DIAS } from '@/club/grupos/tipos';
import { EnvolturaDeInforme } from '@/club/informes/Envoltura';
import {
  ocupacionEnPalabras,
  tieneSitio,
  type FilaDeOcupacion,
  type Informe,
} from '@/club/informes/tipos';

/** Menos del 60 % con aforo puesto es una franja que se puede llenar. */
const claseDeOcupacion = (tasa: number | null): string =>
  tasa === null
    ? 'pastilla'
    : tasa >= 0.9
      ? 'pastilla-mal'
      : tasa >= 0.6
        ? 'pastilla-bien'
        : 'pastilla-paused';

export function Ocupacion({ club }: { club: string }) {
  return (
    <EnvolturaDeInforme<FilaDeOcupacion>
      club={club}
      clave="ocupacion"
      ruta="occupancy"
      titulo="Ocupación"
      explicacion={
        <>
          Cuánta gente hay en cada franja de cada sede.{' '}
          <strong>Sirve para saber dónde cabe un grupo nuevo</strong>, así que
          lo primero que dice es qué huecos tienen sitio de verdad.
        </>
      }
      vacio={{
        titulo: 'No hay horarios que medir',
        explicacion:
          'Este informe cruza el horario de los grupos con su aforo. Hace falta al menos un grupo con horario puesto.',
      }}
      pedir={async () => {
        const { data, error } = await api.v1
          .clubs({ club })
          .reports.occupancy.get({ query: {} });
        if (error) throw new ErrorDeApi(aProblema(error));
        return data as unknown as Informe<FilaDeOcupacion>;
      }}
    >
      {(datos) => {
        const conSitio = datos.rows.filter((f) => tieneSitio(f));
        const sinAforo = datos.rows.filter((f) => f.capacity === null);

        return (
          <>
            {/* La respuesta a la pregunta que trae aquí, antes de la tabla. */}
            {conSitio.length > 0 ? (
              <div className="aviso aviso-calma" role="status">
                <strong>
                  {conSitio.length === 1
                    ? 'Hay una franja con sitio de sobra:'
                    : `Hay ${conSitio.length} franjas con sitio de sobra:`}
                </strong>{' '}
                {conSitio
                  .slice(0, 4)
                  .map(
                    (f) =>
                      `${DIAS[f.weekday] ?? f.weekday} a las ${f.starts_at} en ${f.venue_name} (${f.free_seats} plazas)`
                  )
                  .join('; ')}
                {conSitio.length > 4 ? ', y alguna más abajo' : ''}.
              </div>
            ) : (
              <div className="aviso" role="status">
                <strong>No hay ninguna franja con cinco plazas libres.</strong>{' '}
                Para abrir un grupo nuevo habría que buscar otro horario, otra
                sede, o mirar si algún aforo está puesto por debajo de lo que
                cabe.
              </div>
            )}

            {/*
              Las franjas sin aforo, dichas aparte: no son huecos ni son llenos, son
              desconocidas, y son las que hacen que este informe no sirva.
            */}
            {sinAforo.length > 0 && (
              <p className="tenue">
                {sinAforo.length === 1
                  ? 'Una franja no tiene aforo puesto'
                  : `${sinAforo.length} franjas no tienen aforo puesto`}
                , así que no se sabe si les cabe alguien. El aforo se pone en el
                grupo, y es lo que hace útil esta pantalla.
              </p>
            )}

            <div className="tabla-envoltura">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Sede</th>
                    <th>Cuándo</th>
                    <th className="num">Grupos</th>
                    <th>Ocupación</th>
                    <th className="num">Plazas libres</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.rows.map((f, i) => (
                    <tr
                      key={`${f.venue_name}-${f.weekday}-${f.starts_at}-${i}`}
                    >
                      <td>{f.venue_name}</td>
                      <td>
                        {/* `weekday` es ISO: `DIAS[1]` es lunes. */}
                        {DIAS[f.weekday] ?? `Día ${f.weekday}`} · {f.starts_at}–
                        {f.ends_at}
                      </td>
                      <td className="num">{f.groups}</td>
                      <td>
                        <span
                          className={`pastilla ${claseDeOcupacion(f.occupancy_rate)}`}
                        >
                          {ocupacionEnPalabras(f)}
                        </span>
                      </td>
                      <td className="num">
                        {f.free_seats === null ? '—' : f.free_seats}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        );
      }}
    </EnvolturaDeInforme>
  );
}
